/**
 * Dashboard filter interface for type-safe query keys
 * 
 * @property userIds - Filter by user IDs
 * @property projectIds - Filter by project IDs
 * @property tags - Filter by tags
 */
export interface DashboardFilters {
  userIds?: string[];
  projectIds?: string[];
  tags?: string[];
}

/**
 * Query key factory for dashboard
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to dashboard. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - dashboardKeys.all: Invalidates all dashboard-related queries
 * - dashboardKeys.content(): Invalidates the main dashboard content
 * - dashboardKeys.content(filters): Invalidates specific filtered dashboard view
 * - dashboardKeys.relevance(): Invalidates the dashboard relevance check
 * - dashboardKeys.section(): Invalidates all dashboard sections
 * - dashboardKeys.section(sectionName): Invalidates a specific dashboard section
 * - dashboardKeys.byUser(userId): Invalidates dashboard content for a specific user
 * 
 * Usage examples:
 * - dashboardKeys.all - Base key for all dashboard data
 * - dashboardKeys.content() - Key for dashboard content without filters
 * - dashboardKeys.content({ userIds: ['user-123'] }) - Key for filtered dashboard view
 * - dashboardKeys.section('overdue') - Key for the overdue tasks section
 * 
 * @example
 * // Invalidate all dashboard data
 * queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
 * 
 * @example
 * // Fetch dashboard content
 * useQuery({ queryKey: dashboardKeys.content(), queryFn: fetchDashboardContent });
 * 
 * @example
 * // Invalidate just the overdue section
 * queryClient.invalidateQueries({ queryKey: dashboardKeys.section('overdue') });
 */
export const dashboardKeys = {
  /**
   * Base key for all dashboard-related queries
   * Invalidating this key will invalidate all dashboard queries
   */
  all: ['dashboard'] as const,
  
  /**
   * Key for dashboard content
   * 
   * @param filters - Optional filters to apply
   * @returns Tuple of ['dashboard', 'content'] or ['dashboard', 'content', filters]
   */
  content: (filters?: DashboardFilters) => 
    filters ? [...dashboardKeys.all, 'content', filters] as const : [...dashboardKeys.all, 'content'] as const,
  
  /**
   * Key for dashboard relevance check
   * Used for determining if dashboard view is relevant for the user
   * 
   * @returns Tuple of ['dashboard', 'relevance']
   */
  relevance: () => [...dashboardKeys.all, 'relevance'] as const,
  
  /**
   * Key for dashboard sections (highlighted, overdue, today, thisWeek)
   * Allows for more granular cache invalidation per section
   * 
   * @param section - Optional section name to target specific section
   * @returns Tuple of ['dashboard', 'section'] or ['dashboard', 'section', sectionName]
   */
  section: (section?: 'highlighted' | 'overdue' | 'today' | 'thisWeek') => 
    section ? [...dashboardKeys.all, 'section', section] as const : [...dashboardKeys.all, 'section'] as const,
  
  /**
   * Key for user-specific dashboard views
   * Used to invalidate dashboard content for a specific user
   * 
   * @param userId - User ID
   * @returns Tuple of ['dashboard', 'by-user', userId]
   */
  byUser: (userId: string) => [...dashboardKeys.all, 'by-user', userId] as const,
  
  /**
   * Key for dashboard task details
   * Used for caching dashboard task details
   * 
   * @param taskId - Task ID
   * @returns Tuple of ['dashboard', 'task', taskId]
   */
  task: (taskId: string) => [...dashboardKeys.all, 'task', taskId] as const,
  
  /**
   * Key for filtered dashboard content
   * Similar to content() but specifically for post-filter results
   * This matches the pattern used in other query implementations
   * 
   * @param filters - Dashboard filters to apply
   * @returns Tuple of ['dashboard', 'filtered-content', filters]
   */
  filteredContent: (filters: DashboardFiltersState) => 
    [...dashboardKeys.all, 'filtered-content', filters] as const,
};